import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Heart, Send, Check, ArrowLeft, Shield, Sparkles, Linkedin } from 'lucide-react';
import { toast } from 'sonner';
import BrandTitle from '../components/BrandTitle';

const API = process.env.REACT_APP_BACKEND_URL;

const ShareYourVoice = () => {
  const [form, setForm] = useState({
    name: '',
    role: '',
    company: '',
    email: '',
    quote: '',
    linkedin_url: '',
    consent_public_display: true,
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canSubmit =
    form.name.trim().length >= 2 &&
    form.quote.trim().length >= 10 &&
    !loading;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    try {
      await axios.post(`${API}/api/testimonials/submit`, {
        ...form,
        email: form.email || null,
        role: form.role || null,
        company: form.company || null,
        linkedin_url: form.linkedin_url || null,
      });
      setSubmitted(true);
      toast.success('Thank you — your words mean everything 💚');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Something went wrong. Please try again.';
      toast.error(typeof msg === 'string' ? msg : 'Submission failed');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center px-4">
        <div className="max-w-lg w-full text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-500/15 border border-emerald-500/30 mb-6">
            <Check className="w-10 h-10 text-emerald-300" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold mb-4"><BrandTitle>Thank you, truly.</BrandTitle></h1>
          <p className="text-slate-300 leading-relaxed mb-8">
            I read every single message that comes in — and I'll personally approve and feature
            your words on the site. If you left your LinkedIn, I'll send you a thank-you note.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link
              to="/about"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/[0.05] hover:bg-white/[0.08] border border-white/10 text-sm font-semibold text-white transition-all"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to our story
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-500 hover:bg-emerald-600 text-sm font-semibold text-white transition-all"
            >
              Try CARTIEAI
              <Sparkles className="w-4 h-4" />
            </Link>
          </div>
          <p className="text-xs text-slate-300 mt-8">— Lakshmi Kiranmai Guduru, Founder</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      {/* Hero */}
      <section className="pt-16 sm:pt-24 pb-8 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <Link
            to="/about"
            className="inline-flex items-center gap-1.5 text-xs text-slate-300 hover:text-slate-200 mb-6 transition-colors"
          >
            <ArrowLeft className="w-3 h-3" />
            Back to our story
          </Link>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-4">
            <Heart className="w-3 h-3 text-emerald-300" />
            <span className="text-[11px] font-semibold tracking-widest uppercase text-emerald-300">
              Share your voice
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-4">
            Tried CARTIEAI? <br />
            <span className="text-emerald-600">
              Help me tell the world.
            </span>
          </h1>
          <p className="text-base text-slate-300 max-w-lg mx-auto leading-relaxed">
            I'm a solo founder. Every honest quote — good or critical — helps me build something
            that truly matters. One sentence is enough. No marketing fluff needed.
          </p>
        </div>
      </section>

      {/* Form */}
      <section className="py-12 px-4">
        <div className="max-w-xl mx-auto">
          <form
            onSubmit={handleSubmit}
            className="relative rounded-2xl border border-white/10 bg-white/[0.02] p-6 sm:p-8 space-y-5"
            data-testid="testimonial-form"
          >
            {/* Ambient glow */}
            <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-emerald-500/10 blur-3xl pointer-events-none" />

            {/* Quote (most important — first) */}
            <div className="relative">
              <label className="flex items-center justify-between text-xs font-semibold text-slate-200 mb-2">
                <span>Your honest thoughts<span className="text-rose-400 ml-0.5">*</span></span>
                <span className="text-[11px] text-slate-300 font-normal">{form.quote.length}/500</span>
              </label>
              <textarea
                value={form.quote}
                onChange={(e) => update('quote', e.target.value.slice(0, 500))}
                placeholder="e.g., I've been looking for something like this for years. It's the first FinOps tool that actually explains WHY the bill went up."
                className="w-full bg-slate-900/50 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-300 focus:border-emerald-500/50 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 resize-none"
                rows={4}
                required
                minLength={10}
                data-testid="testimonial-quote"
              />
              <p className="text-[11px] text-slate-300 mt-1.5">
                Write like you'd message a friend. One sentence is perfect.
              </p>
            </div>

            {/* Name — required */}
            <div className="relative">
              <label className="block text-xs font-semibold text-slate-200 mb-2">
                Your name<span className="text-rose-400 ml-0.5">*</span>
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => update('name', e.target.value)}
                placeholder="e.g., Priya S."
                className="w-full bg-slate-900/50 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-slate-300 focus:border-emerald-500/50 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                required
                minLength={2}
                data-testid="testimonial-name"
              />
            </div>

            {/* Role + Company grid */}
            <div className="relative grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-200 mb-2">Your role</label>
                <input
                  type="text"
                  value={form.role}
                  onChange={(e) => update('role', e.target.value)}
                  placeholder="Director of FinOps"
                  className="w-full bg-slate-900/50 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-slate-300 focus:border-emerald-500/50 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                  data-testid="testimonial-role"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-200 mb-2">Company</label>
                <input
                  type="text"
                  value={form.company}
                  onChange={(e) => update('company', e.target.value)}
                  placeholder="Optional"
                  className="w-full bg-slate-900/50 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-slate-300 focus:border-emerald-500/50 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                  data-testid="testimonial-company"
                />
              </div>
            </div>

            {/* Email + LinkedIn grid */}
            <div className="relative grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-200 mb-2">Email</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => update('email', e.target.value)}
                  placeholder="Optional — for a thank-you"
                  className="w-full bg-slate-900/50 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-slate-300 focus:border-emerald-500/50 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                  data-testid="testimonial-email"
                />
              </div>
              <div>
                <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-200 mb-2">
                  <Linkedin className="w-3 h-3 text-[#0A66C2]" />
                  LinkedIn
                </label>
                <input
                  type="url"
                  value={form.linkedin_url}
                  onChange={(e) => update('linkedin_url', e.target.value)}
                  placeholder="Optional"
                  className="w-full bg-slate-900/50 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-slate-300 focus:border-emerald-500/50 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                  data-testid="testimonial-linkedin"
                />
              </div>
            </div>

            {/* Consent */}
            <label className="relative flex items-start gap-2.5 cursor-pointer group">
              <input
                type="checkbox"
                checked={form.consent_public_display}
                onChange={(e) => update('consent_public_display', e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded accent-emerald-500 cursor-pointer"
                data-testid="testimonial-consent"
              />
              <span className="text-xs text-slate-300 leading-relaxed group-hover:text-slate-200 transition-colors">
                It's okay to feature this on CARTIEAI's website and social channels with my name and role.
              </span>
            </label>

            {/* Privacy reassurance */}
            <div className="relative flex items-start gap-2 text-[11px] text-slate-300 pt-2 border-t border-white/5">
              <Shield className="w-3 h-3 text-emerald-300/60 flex-shrink-0 mt-0.5" />
              <span>
                Your email is private and will never be displayed. I personally review every submission.
              </span>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={!canSubmit}
              className={`relative w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all ${
                canSubmit
                  ? 'bg-emerald-500 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white shadow-lg shadow-emerald-500/20 hover:-translate-y-0.5'
                  : 'bg-white/[0.05] text-slate-300 cursor-not-allowed'
              }`}
              data-testid="testimonial-submit"
            >
              {loading ? (
                <>
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Share my voice
                </>
              )}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default ShareYourVoice;
